/**
 * AddPlayersRow container.
 *
 * Exists to bind the list-level callbacks (`onAdd(playerId)`,
 * `onDismiss(suggestionId)`) into the zero-argument handlers the View renders,
 * with stable identities. Following the `ReviewQueueRow` precedent keeps the
 * per-item closures out of the list View's `map`, which `react-perf` bans.
 * @module features/player-kanban/components/molecules/AddPlayersRow
 */

import React, { useCallback } from "react";

import type { SuggestionReasonCategory } from "@/generated/graphql";

import AddPlayersRowView from "./AddPlayersRowView";

/** Props for the AddPlayersRow container. */
interface AddPlayersRowContainerProps {
  /** Player id — the add target and the row's test identity. */
  readonly playerId: string;
  /**
   * The candidate's stable `PipelineSuggestedPlayer.id`, which is what a
   * dismissal is keyed on. Absent on search rows, which cannot be dismissed.
   */
  readonly suggestionId?: string;
  /** Display name. */
  readonly name: string;
  /** Pre-composed secondary line (club · age). */
  readonly meta: string;
  /** SE-6945: GPR score for the row's inline info pill; null when unscored. */
  readonly gpr?: string | null;
  /** Reason categories, for Suggested rows only. */
  readonly reasons?: readonly SuggestionReasonCategory[];
  /** Whether an add for this player is in flight. */
  readonly isAdding: boolean;
  /** SE-6948: whether the player already sits in this pipeline (muted row). */
  readonly isInList?: boolean;
  /** Add this player to the pipeline. */
  readonly onAdd: (playerId: string) => void;
  /** Hide this candidate for this session. Fires no network request. */
  readonly onDismiss?: (suggestionId: string) => void;
}

/**
 * Render one candidate row.
 * @param root0 - Component props.
 * @param root0.playerId - Player id.
 * @param root0.suggestionId - Stable suggestion id (Suggested rows only).
 * @param root0.name - Display name.
 * @param root0.meta - Secondary line.
 * @param root0.gpr - GPR score for the inline info pill (SE-6945).
 * @param root0.reasons - Reason categories.
 * @param root0.isAdding - Whether an add is in flight.
 * @param root0.isInList - Whether the player already sits in this pipeline (SE-6948).
 * @param root0.onAdd - Add callback.
 * @param root0.onDismiss - Dismiss callback.
 */
const AddPlayersRowContainer = ({
  playerId,
  suggestionId,
  name,
  meta,
  gpr,
  reasons,
  isAdding,
  isInList,
  onAdd,
  onDismiss,
}: AddPlayersRowContainerProps) => {
  const handleAdd = useCallback(() => onAdd(playerId), [onAdd, playerId]);

  const handleDismiss = useCallback(() => {
    if (onDismiss && suggestionId) onDismiss(suggestionId);
  }, [onDismiss, suggestionId]);

  return (
    <AddPlayersRowView
      playerId={playerId}
      name={name}
      meta={meta}
      gpr={gpr}
      reasons={reasons}
      isAdding={isAdding}
      isInList={isInList}
      onAdd={handleAdd}
      onDismiss={onDismiss && suggestionId ? handleDismiss : undefined}
    />
  );
};

export default AddPlayersRowContainer;
